import {Bindable} from "./Bindable";
import Deferred from "./Deferred";
import app from "./App";
import dbg from "./Debug";
import util from "./Util";
import ui from "./UI";
import keyboard from "./Keyboard";
import mouse from "./Mouse";

/**
 * Provides functionality to navigate between module actions.
 *
 * Can fire the following events:
 *
 *    > PRE_NAVIGATE - fired before navigating to a new module action
 *        module - module name
 *        action - action name
 *        parameters - action parameters
 *    > POST_NAVIGATE - fired after navigating to a new module action
 *        module - module name
 *        action - action name
 *        parameters - action parameters
 *    > PRE_PARTIAL - fired before opening a partial view
 *        containerSelector - selector for the partial container
 *        module - module name
 *        action - action name
 *        parameters - action parameters
 *    > POST_PARTIAL - fired after opening a partial view
 *        containerSelector - selector for the partial container
 *        module - module name
 *        action - action name
 *        parameters - action parameters
 *  > URL_CHANGED - fired when the URL changes
 *        parameters - URL parameters (url, hash, path, query, args, host, port, protocol)
 *
 * @class Navi
 * @extends Bindable
 * @constructor
 * @module Core
 */
export class CoreNavi extends Bindable implements Navi {
    $route;
    $location;
    $routeParams;
    $rootScope;

    /**
     * Event types.
     *
     * @event
     * @param {Object} Event
     * @param {String} Event.PRE_NAVIGATE Triggered just before navigation
     * @param {String} Event.POST_NAVIGATE Triggered just after navigation
     * @param {String} Event.PRE_PARTIAL Triggered just before opening partial
     * @param {String} Event.POST_PARTIAL Triggered just after opening partial
     * @param {String} Event.STACK_CHANGED Called when navigation stack updates
     * @param {String} Event.URL_CHANGED Called when URL changes
     * @param {String} Event.PARAMETERS_CHANGED Called when current action parameters change
     * @param {String} Event.SLEEP Called on scope when action is put to sleep
     * @param {String} Event.WAKEUP Called on scope when action is awaken
     * @param {String} Event.EXIT Called on scope when action is killed
     */
    Event = {
        PRE_NAVIGATE: 'pre-navigate',
        START_NAVIGATE: 'start-navigate',
        POST_NAVIGATE: 'post-navigate',
        PRE_PARTIAL: 'pre-partial',
        POST_PARTIAL: 'post-partial',
        URL_CHANGED: 'url-changed',
    };


    /**
     * Initiates the components.
     *
     * @method init
     * @return {Navi} Self
     */
    init() {
        this.bindApplicationEvents();
        this.bindRouterEvents();
        return this;
    }

    bindApplicationEvents() {
        let that = this;
        keyboard.bind([keyboard.Event.KEYDOWN, keyboard.Event.KEYUP], (e) => {
            that._onKeyEvent(e.info);
        });

        mouse.bind([mouse.Event.MOUSEDOWN, mouse.Event.MOUSEUP, mouse.Event.MOUSEMOVE], (e) => {
            that._onMouseEvent(e.info);
        });
    }

    bindRouterEvents() {
        let that = this;
        that.$rootScope.$on('$routeChangeStart', (evt, newRoute, oldRoute) => {
            dbg.log('$routeChangeStart received in navi: ' + evt + ', route: ' + newRoute.component + ', path: ' + newRoute.originalPath);

            let tracker = {
                allowNavigation: true,// allows/blocks navigation to target components
                reroute: null // can reroute to a different components if target components is not allowed
            };
            that.fire({
                type: that.Event.START_NAVIGATE,
                component: newRoute.component,
                parameters: newRoute.parameters,
                oldRoute,
                newRoute,
                tracker
            });

            if (!tracker.allowNavigation) {
                if (!tracker.reroute) {
                    that.openHome();
                } else {
                    that.open(tracker.reroute.component, tracker.reroute.params);
                }
            }

            //
            if (!!oldRoute && !!oldRoute.scope) {
                let rtCtrlScope = oldRoute.scope.$$childHead;
                if (!!rtCtrlScope) {
                    do {
                        Object.keys(rtCtrlScope)
                            .filter(key => key.charAt(0) !== '$')
                            .forEach(key => {
                                if (!!rtCtrlScope[key]['destroy']) !!rtCtrlScope[key]['destroy']();
                            });
                    } while (!!(rtCtrlScope = rtCtrlScope.$$nextSibling))
                }
            }
        });
        that.$rootScope.$on('$routeChangeSuccess', (evt, newRoute) => {
            that.fire({
                type: that.Event.POST_NAVIGATE,
                component: newRoute.component,
                parameters: newRoute.parameters
            });

            dbg.log('$routeChangeSuccess received in navi: ' + evt + ', route: ' + newRoute.component + ', path: ' + newRoute.originalPath);
        });
        that.$rootScope.$on('$routeChangeError', (evt, route) => {
            dbg.log('$routeChangeError received in navi: ' + evt + ', route: ' + route);
        });
        that.$rootScope.$on('$routeUpdate', (evt, route) => {
            dbg.log('$routeUpdate received in navi: ' + evt + ', route: ' + route);
        });

        that.$rootScope.$watch(
            () => that.$location.absUrl(),
            () => that.fire({type: that.Event.URL_CHANGED})
        );
    }

    getParameter(key) {
        return this.$routeParams[key] || this.getCurrent()[key];
    }


    /**
     * Navigates to a module action.
     *
     * By default the action is opened in container defined by the configuration property viewSelector.
     *
     * @method open
     * @param {String} module Module to open
     * @param {String} [action=index] Action to navigate to
     * @param {Array} [parameters] Action parameters
     */
    open(component, parameters?): Promise<any> {
        let tracker = {canDeactivate: Promise.resolve()};
        this.fire({type: this.Event.PRE_NAVIGATE, component, parameters, tracker});

        return new Promise((resolve, reject) => {
            tracker.canDeactivate.then(
                () => {
                    let binding = this.bind(this.Event.POST_NAVIGATE, () => {
                        resolve();
                        binding.unbind();
                    });
                    this.$location.path($RouteConfig.routeUrl(component, parameters)).search({});
                    app.validate();
                },
                () => {
                    /* can't leave current components*/
                    dbg.log(`Navigation to ${component} cancelled`);
                    reject(`Navigation to ${component} cancelled`);
                }
            )
        });
    }

    private home = 'epit-dashboard';

    setHomeComponent(home: string): void {
        this.home = home;
    }

    openHome() {
        this.open(this.home);
    }

    isHome(): boolean {
        return this.getCurrent() && this.getCurrent().component === this.home;
    }

    isProxyView() {
        return this.$route.current && this.$route.current.proxy;
    }

    reloadRoute() {
        this.$location.search({_: Date.now()});
        app.validate();
        //this.$route.reload();
    }

    /**
     * Opens a partial view.
     *
     * Partials can be used to load module actions in any container to display main menu etc.
     *
     * @method partial
     * @param {String} containerSelector Container selector
     * @param {String} component to open
     * @param {Object} [parameters] components route parameters
     * @return {Deferred} Deferred promise
     */
    partial(containerSelector, component, parameters) {
        parameters = parameters || [];

        let that = this,
            deferred = new Deferred();

        that.fire({
            type: this.Event.PRE_PARTIAL,
            containerSelector: containerSelector,
            component: component,
            parameters: parameters
        });

        app.parameters = parameters;
        let item = ui.showPartial(component, parameters, containerSelector);

        that.fire({
            type: that.Event.POST_PARTIAL,
            containerSelector: containerSelector,
            component: component,
            parameters: parameters
        });

        deferred.resolve(item);

        app.validate();

        return deferred.promise();
    }

    openUrl(url) {
        var parts = url.split('?');
        var base = util.substringBefore(app.location.absUrl(), app.location.path());
        app.location.path(util.substringAfter(parts[0], base));
        if (parts.length > 1) {
            app.location.search(parts[1]);
        }
        app.validate();
    }

    /**
     * Navigates back to previous action.
     *
     * @method back
     */
    back() {
        window.history.back();
    }

    /**
     * Returns currently active action info.
     *
     * @method getCurrent
     * @return {Object|null}
     */
    getCurrent() {
        return this.$route.current;
    }

    /**
     * Triggered on key events.
     *
     * Passes the key event on to currently active module action controller.
     *
     * @method _onKeyEvent
     * @param {Keyboard.KeyEvent} event Key event
     * @private
     */
    _onKeyEvent(event) {
        var currentItem = this.getCurrent();

        if (currentItem === null || !util.isFunction(currentItem.fire)) {
            return;
        }

        currentItem.fire(event.type, event);
    }

    /**
     * Triggered on mouse events.
     *
     * Passes the key event on to currently active module action controller.
     *
     * @method _onMouseEvent
     * @param {Mouse.MouseEvent} event Key event
     * @private
     */
    _onMouseEvent(event) {
        var currentItem = this.getCurrent();

        if (currentItem === null || !util.isFunction(currentItem.fire)) {
            return;
        }

        currentItem.fire(event.type, event);
    }
}

export default new CoreNavi();


// WEBPACK FOOTER //
// ./src/main/ui/core/application/Navi.ts